import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  InputGroup,
  Offcanvas,
  OverlayTrigger,
  Row,
  Spinner,
  Table,
  Tooltip,
} from 'react-bootstrap';
import {
  Building,
  Check,
  Check2Circle,
  Funnel,
  Person,
  Search,
  Star,
  StarFill,
  ThreeDotsVertical,
} from 'react-bootstrap-icons';
import {
  useCreateTaskMutation,
  useGetProjectsQuery,
  useGetTasksQuery,
  useGetUsersQuery,
  useGetUserPreferencesQuery,
  useUpdateTaskMutation,
  useUpdateUserPreferencesMutation,
} from '../../services/api';
import { useUser } from '../../contexts/UserContext';
import {
  preferencesTenantWriteSpread,
  useTenantIdForPreferences,
  useUserPreferencesQueryArg,
} from '../../hooks/useTenantIdForPreferences';
import {
  getTenantTimezoneFromPreferencesData,
  toLocaleDateStringInTimezone,
} from '../../utils/tenantTimezone';
import {
  normalizeDepartmentAssignments,
  parseErmDepartmentPreferences,
} from './departmentPreferences';
import ERMSectionHeader from './ERMSectionHeader';

type TaskStatusFilter = 'all' | 'todo' | 'done';
type AssigneeFilter = 'all' | 'me' | 'unassigned';
type OwnerFilterPanelTab = 'favorites' | 'owners' | 'filters';
type TaskAssigneeScopeFilterId = 'all' | 'me' | 'unassigned';

type TaskAssigneeScopeFilterOption = {
  id: TaskAssigneeScopeFilterId;
  label: string;
  description: string;
};

interface ErmTask {
  id: string;
  projectId?: string;
  phase?: string;
  subject?: string;
  ownerId?: string;
  visibility?: 'global' | 'personal';
  assigneeId?: string;
  dueDate?: string;
  done?: boolean;
  group?: string;
  sortOrder?: number;
  description?: string;
  archived?: boolean;
  archivedAt?: string;
  [key: string]: unknown;
}

interface ErmProject {
  id: string;
  title?: string;
  ownerId?: string;
  [key: string]: unknown;
}

interface TaskDraft {
  id: string | null;
  projectId: string;
  subject: string;
  phase: string;
  group: string;
  dueDate: string;
  ownerId: string;
  visibility: 'global' | 'personal';
  assigneeId: string;
  description: string;
  done: boolean;
}

const PHASE_OPTIONS = ['Phase unassigned', 'Kick-off', 'Planning', 'Implementation', 'Review', 'Closing'];
const DEFAULT_GROUP = 'Ungrouped';
const TASK_SUBJECT_PREVIEW_CHAR_LIMIT = 24;
const DEFAULT_OWNER_FILTER_LABEL = 'Everyone';
const DEFAULT_SELF_OWNER_LABEL = 'PhiLL Kross';
const OWNER_EVERYONE_ID = '__everyone__';
const TASK_OWNER_FAVORITES_STORAGE_KEY = 'ermTaskOwnerFavorites';
const TASK_FILTER_FAVORITES_STORAGE_KEY = 'ermTaskFilterFavorites';

const TASK_ASSIGNEE_SCOPE_FILTER_OPTIONS: TaskAssigneeScopeFilterOption[] = [
  { id: 'all', label: 'All assignees', description: 'Show tasks for everyone' },
  { id: 'me', label: 'Assigned to me', description: 'Only tasks assigned to you' },
  { id: 'unassigned', label: 'Unassigned', description: 'Only tasks with no assignee' },
];

const normalizeOwnerKey = (value: string): string =>
  String(value || '').replace(/\(\s*you\s*\)/gi, '').trim().toLowerCase();
const toRecord = (value: unknown): Record<string, unknown> => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
);

function toDateInputValue(value: unknown): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const parsed = Date.parse(raw);
  if (!Number.isFinite(parsed)) return '';
  return new Date(parsed).toISOString().slice(0, 10);
}

function parseDueSortValue(task: ErmTask): number {
  const raw = String(task?.dueDate || '').trim();
  if (!raw) return Number.POSITIVE_INFINITY;
  const parsed = Date.parse(raw);
  if (!Number.isFinite(parsed)) return Number.POSITIVE_INFINITY;
  return parsed;
}

function createDraft(args: {
  projectId: string;
  ownerId: string;
  visibility: 'global' | 'personal';
  assigneeId: string;
}): TaskDraft {
  return {
    id: null,
    projectId: args.projectId,
    subject: '',
    phase: 'Phase unassigned',
    group: DEFAULT_GROUP,
    dueDate: '',
    ownerId: args.ownerId,
    visibility: args.visibility,
    assigneeId: args.assigneeId,
    description: '',
    done: false,
  };
}

function getTaskSubjectDisplay(value: unknown): string {
  const subject = String(value || '').trim();
  if (!subject) return 'Untitled task';
  if (subject.length <= TASK_SUBJECT_PREVIEW_CHAR_LIMIT) return subject;
  const sliceLength = Math.max(0, TASK_SUBJECT_PREVIEW_CHAR_LIMIT - 3);
  return `${subject.slice(0, sliceLength).trimEnd()}...`;
}

export default function ERMTasks() {
  const { currentUser } = useUser();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<TaskStatusFilter>('todo');
  const [assigneeFilter, setAssigneeFilter] = useState<AssigneeFilter>('all');
  const [showOwnerFilter, setShowOwnerFilter] = useState(false);
  const [ownerFilterTab, setOwnerFilterTab] = useState<OwnerFilterPanelTab>('owners');
  const [ownerFilterSearchQuery, setOwnerFilterSearchQuery] = useState('');
  const [hoveredOwnerRowKey, setHoveredOwnerRowKey] = useState<string | null>(null);
  const [selectedOwnerId, setSelectedOwnerId] = useState<string>(OWNER_EVERYONE_ID);
  const [favoriteOwnerKeys, setFavoriteOwnerKeys] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(TASK_OWNER_FAVORITES_STORAGE_KEY);
      const parsed = stored ? JSON.parse(stored) : [];
      return Array.isArray(parsed)
        ? parsed.map((entry) => String(entry || '').trim()).filter(Boolean)
        : [];
    } catch {
      return [];
    }
  });
  const [favoriteAssigneeFilterIds, setFavoriteAssigneeFilterIds] = useState<TaskAssigneeScopeFilterId[]>(() => {
    try {
      const stored = localStorage.getItem(TASK_FILTER_FAVORITES_STORAGE_KEY);
      const parsed = stored ? JSON.parse(stored) : [];
      if (!Array.isArray(parsed)) return [];
      const allowed = new Set<TaskAssigneeScopeFilterId>(['all', 'me', 'unassigned']);
      return parsed
        .map((entry) => String(entry) as TaskAssigneeScopeFilterId)
        .filter((entry) => allowed.has(entry));
    } catch {
      return [];
    }
  });
  const [showEditor, setShowEditor] = useState(false);
  const [draft, setDraft] = useState<TaskDraft>(() => createDraft({
    projectId: '',
    ownerId: '',
    visibility: 'global',
    assigneeId: '',
  }));
  const [isSavingProjectDepartmentRouting, setIsSavingProjectDepartmentRouting] = useState(false);

  const taskQueryArg = useMemo(() => ({ scope: 'visible', assignee: 'all', archived: false }), []);

  const { data: rawTasks = [], isLoading: isLoadingTasks, refetch: refetchTasks } = useGetTasksQuery(taskQueryArg);
  const { data: projects = [], isLoading: isLoadingProjects } = useGetProjectsQuery({});
  const { data: usersData } = useGetUsersQuery(undefined);
  const tenantIdForPrefs = useTenantIdForPreferences();
  const userPreferencesQueryArg = useUserPreferencesQueryArg();
  const { data: userPreferencesData, refetch: refetchUserPreferences } =
    useGetUserPreferencesQuery(userPreferencesQueryArg);
  const [createTask, { isLoading: isCreatingTask }] = useCreateTaskMutation();
  const [updateTask, { isLoading: isUpdatingTask }] = useUpdateTaskMutation();
  const [updateUserPreferences] = useUpdateUserPreferencesMutation();
  const normalizedRole = String(currentUser?.role || '').trim().toLowerCase();
  const isAdminUser = normalizedRole === 'admin' || normalizedRole === 'administrator';

  const tenantTimezone = useMemo(
    () => getTenantTimezoneFromPreferencesData(userPreferencesData),
    [userPreferencesData]
  );

  const normalizedProjects = useMemo<ErmProject[]>(
    () => (Array.isArray(projects) ? (projects as ErmProject[]) : []),
    [projects]
  );
  const users = useMemo<Record<string, unknown>[]>(
    () => (
      Array.isArray((usersData as any)?.data?.users)
        ? ((usersData as any).data.users as Record<string, unknown>[])
        : []
    ),
    [usersData]
  );

  const allPreferences = useMemo<Record<string, unknown>>(
    () => toRecord((userPreferencesData as any)?.preferences),
    [userPreferencesData]
  );

  const {
    departments,
    projectDepartmentMap,
    focusedDepartmentIds,
  } = useMemo(
    () => parseErmDepartmentPreferences(allPreferences),
    [allPreferences]
  );

  const projectById = useMemo(() => {
    const map = new Map<string, ErmProject>();
    normalizedProjects.forEach((project) => {
      const id = String(project?.id || '').trim();
      if (!id) return;
      map.set(id, project);
    });
    return map;
  }, [normalizedProjects]);

  const departmentById = useMemo(() => {
    const map = new Map<string, string>();
    departments.forEach((department) => {
      map.set(department.id, department.name);
    });
    return map;
  }, [departments]);
  const sortedDepartments = useMemo(
    () => [...departments].sort((left, right) => (
      String(left?.name || '').localeCompare(String(right?.name || ''), undefined, { sensitivity: 'base' })
    )),
    [departments]
  );

  const validDepartmentIds = useMemo(
    () => new Set(departments.map((department) => department.id)),
    [departments]
  );
  const focusedDepartmentIdSet = useMemo(
    () => new Set(focusedDepartmentIds),
    [focusedDepartmentIds]
  );
  const sortedFocusedDepartmentIds = useMemo(
    () => [...focusedDepartmentIds].sort((left, right) => {
      const leftName = String(departmentById.get(left) || '');
      const rightName = String(departmentById.get(right) || '');
      return leftName.localeCompare(rightName, undefined, { sensitivity: 'base' });
    }),
    [focusedDepartmentIds, departmentById]
  );

  const inferredCurrentUserId = useMemo(() => {
    const fromTasks = (Array.isArray(rawTasks) ? rawTasks : []).find((task: any) => String(task?.assigneeId || '').trim());
    if (fromTasks?.assigneeId) return String(fromTasks.assigneeId).trim();
    const fromProjects = normalizedProjects.find((project) => String(project?.ownerId || '').trim());
    if (fromProjects?.ownerId) return String(fromProjects.ownerId).trim();
    return '';
  }, [rawTasks, normalizedProjects]);

  const currentUserId = String(currentUser?.id || '').trim() || inferredCurrentUserId;
  const selfOwnerBaseLabel = useMemo(() => {
    const explicitName = String(currentUser?.name || '').trim();
    const combinedName = `${String(currentUser?.firstName || '').trim()} ${String(currentUser?.lastName || '').trim()}`.trim();
    return explicitName || combinedName || DEFAULT_SELF_OWNER_LABEL;
  }, [currentUser?.firstName, currentUser?.lastName, currentUser?.name]);
  const selfOwnerLabel = useMemo(() => `${selfOwnerBaseLabel} (you)`, [selfOwnerBaseLabel]);

  const userLabelById = useMemo(() => {
    const labels = new Map<string, string>();
    users.forEach((userRecord) => {
      const userId = String(userRecord?.id || '').trim();
      if (!userId) return;
      const firstName = String(userRecord?.firstName || '').trim();
      const lastName = String(userRecord?.lastName || '').trim();
      const fullName = `${firstName} ${lastName}`.trim();
      const fallbackName = String(userRecord?.name || userRecord?.email || userId).trim();
      labels.set(userId, fullName || fallbackName || userId);
    });
    if (currentUserId) labels.set(currentUserId, selfOwnerBaseLabel);
    return labels;
  }, [currentUserId, selfOwnerBaseLabel, users]);

  const ownerOptions = useMemo(() => {
    const ownerMap = new Map<string, { id: string; label: string }>();
    ownerMap.set(OWNER_EVERYONE_ID, { id: OWNER_EVERYONE_ID, label: DEFAULT_OWNER_FILTER_LABEL });
    if (currentUserId) {
      ownerMap.set(currentUserId, { id: currentUserId, label: selfOwnerLabel });
    }

    (Array.isArray(rawTasks) ? rawTasks : []).forEach((task: any) => {
      const visibility = String(task?.visibility || '').trim().toLowerCase() === 'personal' ? 'personal' : 'global';
      const ownerId = String(task?.ownerId || '').trim();
      if (visibility !== 'personal' || !ownerId || ownerMap.has(ownerId)) return;
      ownerMap.set(ownerId, {
        id: ownerId,
        label: ownerId === currentUserId ? selfOwnerLabel : (userLabelById.get(ownerId) || ownerId),
      });
    });

    return Array.from(ownerMap.values()).sort((a, b) => {
      if (a.id === OWNER_EVERYONE_ID) return -1;
      if (b.id === OWNER_EVERYONE_ID) return 1;
      return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
    });
  }, [currentUserId, rawTasks, selfOwnerLabel, userLabelById]);

  const selectedOwnerButtonLabel = useMemo(() => {
    const selected = ownerOptions.find((option) => option.id === selectedOwnerId);
    if (!selected) return DEFAULT_OWNER_FILTER_LABEL;
    if (selected.id === OWNER_EVERYONE_ID) return DEFAULT_OWNER_FILTER_LABEL;
    return selected.label.replace(/\(\s*you\s*\)/i, '').trim() || DEFAULT_OWNER_FILTER_LABEL;
  }, [ownerOptions, selectedOwnerId]);

  const normalizedOwnerFilterSearchQuery = ownerFilterSearchQuery.trim().toLowerCase();

  const visibleOwnerOptions = useMemo(() => {
    if (!normalizedOwnerFilterSearchQuery) return ownerOptions;
    return ownerOptions.filter((option) => option.label.toLowerCase().includes(normalizedOwnerFilterSearchQuery));
  }, [normalizedOwnerFilterSearchQuery, ownerOptions]);

  const visibleScopeFilterOptions = useMemo(() => {
    if (!normalizedOwnerFilterSearchQuery) return TASK_ASSIGNEE_SCOPE_FILTER_OPTIONS;
    return TASK_ASSIGNEE_SCOPE_FILTER_OPTIONS.filter((option) => {
      const searchText = `${option.label} ${option.description}`.toLowerCase();
      return searchText.includes(normalizedOwnerFilterSearchQuery);
    });
  }, [normalizedOwnerFilterSearchQuery]);

  const favoriteOwnerOptions = useMemo(() => {
    if (!favoriteOwnerKeys.length) return [];
    const favorites = new Set(favoriteOwnerKeys);
    return ownerOptions.filter((option) => favorites.has(option.id));
  }, [favoriteOwnerKeys, ownerOptions]);

  const favoriteScopeFilterOptions = useMemo(() => {
    if (!favoriteAssigneeFilterIds.length) return [];
    const favorites = new Set<TaskAssigneeScopeFilterId>(favoriteAssigneeFilterIds);
    return TASK_ASSIGNEE_SCOPE_FILTER_OPTIONS.filter((option) => favorites.has(option.id));
  }, [favoriteAssigneeFilterIds]);

  const visibleFavoriteOwnerOptions = useMemo(() => {
    if (!normalizedOwnerFilterSearchQuery) return favoriteOwnerOptions;
    return favoriteOwnerOptions.filter((option) => option.label.toLowerCase().includes(normalizedOwnerFilterSearchQuery));
  }, [favoriteOwnerOptions, normalizedOwnerFilterSearchQuery]);

  const visibleFavoriteScopeFilterOptions = useMemo(() => {
    if (!normalizedOwnerFilterSearchQuery) return favoriteScopeFilterOptions;
    return favoriteScopeFilterOptions.filter((option) => {
      const searchText = `${option.label} ${option.description}`.toLowerCase();
      return searchText.includes(normalizedOwnerFilterSearchQuery);
    });
  }, [favoriteScopeFilterOptions, normalizedOwnerFilterSearchQuery]);

  const tasks = useMemo<ErmTask[]>(() => {
    const list = (Array.isArray(rawTasks) ? rawTasks : []) as ErmTask[];
    return list.map((task) => ({
      ...task,
      id: String(task?.id || ''),
      subject: String(task?.subject || '').trim() || 'Untitled task',
      phase: String(task?.phase || '').trim() || 'Phase unassigned',
      group: String(task?.group || '').trim() || DEFAULT_GROUP,
      ownerId: String(task?.ownerId || '').trim(),
      visibility: String(task?.visibility || '').trim().toLowerCase() === 'personal' ? 'personal' : 'global',
      assigneeId: String(task?.assigneeId || '').trim(),
      dueDate: String(task?.dueDate || '').trim(),
      done: Boolean(task?.done),
      projectId: String(task?.projectId || '').trim(),
      description: String(task?.description || ''),
    }));
  }, [rawTasks]);

  const getTaskDepartmentIds = useCallback((task: ErmTask): string[] => {
    const projectId = String(task?.projectId || '').trim();
    if (!projectId) return [];
    return normalizeDepartmentAssignments(projectDepartmentMap[projectId], validDepartmentIds);
  }, [projectDepartmentMap, validDepartmentIds]);

  const getTaskDepartmentNames = useCallback((task: ErmTask): string[] => (
    getTaskDepartmentIds(task)
      .map((departmentId) => departmentById.get(departmentId) || '')
      .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }))
      .filter(Boolean)
  ), [departmentById, getTaskDepartmentIds]);

  const visibleTasks = useMemo(() => {
    let list = [...tasks];
    if (search.trim()) {
      const query = search.trim().toLowerCase();
      list = list.filter((task) => {
        const subject = String(task?.subject || '').toLowerCase();
        const projectName = String(projectById.get(String(task?.projectId || '').trim())?.title || '').toLowerCase();
        const departmentNames = getTaskDepartmentNames(task).join(' ').toLowerCase();
        return subject.includes(query) || projectName.includes(query) || departmentNames.includes(query);
      });
    }
    if (statusFilter === 'todo') list = list.filter((task) => !task.done);
    if (statusFilter === 'done') list = list.filter((task) => task.done);
    if (selectedOwnerId !== OWNER_EVERYONE_ID) {
      list = list.filter((task) => (
        String(task.visibility || '').trim().toLowerCase() === 'personal'
          && String(task.ownerId || '').trim() === selectedOwnerId
      ));
    }
    if (assigneeFilter === 'me') {
      list = list.filter((task) => (
        currentUserId.length > 0
          && String(task.assigneeId || '').trim() === currentUserId
      ));
    } else if (assigneeFilter === 'unassigned') {
      list = list.filter((task) => !String(task.assigneeId || '').trim());
    }
    if (focusedDepartmentIds.length > 0) {
      list = list.filter((task) => {
        const departmentIds = getTaskDepartmentIds(task);
        return departmentIds.some((departmentId) => focusedDepartmentIdSet.has(departmentId));
      });
    }
    list.sort((a, b) => {
      const dueA = parseDueSortValue(a);
      const dueB = parseDueSortValue(b);
      if (dueA !== dueB) return dueA - dueB;
      const sortA = Number(a.sortOrder || 0);
      const sortB = Number(b.sortOrder || 0);
      if (sortA !== sortB) return sortA - sortB;
      return String(a.id || '').localeCompare(String(b.id || ''));
    });
    return list;
  }, [
    assigneeFilter,
    currentUserId,
    focusedDepartmentIdSet,
    focusedDepartmentIds,
    getTaskDepartmentIds,
    getTaskDepartmentNames,
    projectById,
    search,
    selectedOwnerId,
    statusFilter,
    tasks,
  ]);

  useEffect(() => {
    try {
      localStorage.setItem(TASK_OWNER_FAVORITES_STORAGE_KEY, JSON.stringify(favoriteOwnerKeys));
    } catch {
      // Ignore localStorage failures in restricted browsing modes.
    }
  }, [favoriteOwnerKeys]);

  useEffect(() => {
    try {
      localStorage.setItem(TASK_FILTER_FAVORITES_STORAGE_KEY, JSON.stringify(favoriteAssigneeFilterIds));
    } catch {
      // Ignore localStorage failures in restricted browsing modes.
    }
  }, [favoriteAssigneeFilterIds]);

  useEffect(() => {
    if (selectedOwnerId === OWNER_EVERYONE_ID) return;
    const selectedExists = ownerOptions.some((option) => option.id === selectedOwnerId);
    if (!selectedExists) {
      setSelectedOwnerId(OWNER_EVERYONE_ID);
    }
  }, [ownerOptions, selectedOwnerId]);

  const toggleFavoriteOwner = (ownerId: string) => {
    if (!ownerId || ownerId === OWNER_EVERYONE_ID) return;
    setFavoriteOwnerKeys((prev) => {
      if (prev.includes(ownerId)) return prev.filter((entry) => entry !== ownerId);
      return [...prev, ownerId];
    });
  };

  const toggleFavoriteAssigneeFilter = (filterId: TaskAssigneeScopeFilterId) => {
    if (filterId === 'all') return;
    setFavoriteAssigneeFilterIds((prev) => {
      if (prev.includes(filterId)) return prev.filter((entry) => entry !== filterId);
      return [...prev, filterId];
    });
  };

  const taskCountLabel = `${visibleTasks.length} task${visibleTasks.length === 1 ? '' : 's'}`;
  const activeStatusLabel = statusFilter === 'all' ? 'All statuses' : statusFilter === 'done' ? 'Done' : 'To-do';
  const activeDepartmentLabel = focusedDepartmentIds.length === 0
    ? 'All departments'
    : focusedDepartmentIds.length === 1
      ? (departmentById.get(focusedDepartmentIds[0]) || '1 department')
      : `${focusedDepartmentIds.length} departments`;

  const persistDepartmentFocus = async (nextFocusedDepartmentIds: string[]) => {
    await updateUserPreferences({
      ...preferencesTenantWriteSpread(tenantIdForPrefs),
      preferences: {
        ermFocusedDepartmentIds: normalizeDepartmentAssignments(nextFocusedDepartmentIds, validDepartmentIds),
      },
    }).unwrap();
  };

  const handleToggleFocusedDepartment = async (departmentId: string) => {
    const nextFocused = focusedDepartmentIdSet.has(departmentId)
      ? focusedDepartmentIds.filter((id) => id !== departmentId)
      : [...focusedDepartmentIds, departmentId];
    try {
      await persistDepartmentFocus(nextFocused);
    } catch (error) {
      console.error('Failed to update department focus:', error);
    }
  };

  const handleClearFocusedDepartments = async () => {
    try {
      await persistDepartmentFocus([]);
    } catch (error) {
      console.error('Failed to clear department focus:', error);
    }
  };

  const openCreatePanel = () => {
    const defaultProjectId = String(normalizedProjects[0]?.id || '').trim();
    setDraft(createDraft({
      projectId: defaultProjectId,
      ownerId: currentUserId || inferredCurrentUserId,
      visibility: 'global',
      assigneeId: currentUserId || inferredCurrentUserId,
    }));
    setShowEditor(true);
  };

  const openEditPanel = (task: ErmTask) => {
    setDraft({
      id: task.id,
      projectId: String(task.projectId || ''),
      subject: String(task.subject || ''),
      phase: String(task.phase || 'Phase unassigned'),
      group: String(task.group || DEFAULT_GROUP),
      dueDate: toDateInputValue(task.dueDate),
      ownerId: String(task.ownerId || currentUserId || ''),
      visibility: task.visibility === 'personal' ? 'personal' : 'global',
      assigneeId: String(task.assigneeId || ''),
      description: String(task.description || ''),
      done: Boolean(task.done),
    });
    setShowEditor(true);
  };

  const draftProjectDepartmentIds = useMemo(() => {
    const projectId = String(draft.projectId || '').trim();
    if (!projectId) return [] as string[];
    return normalizeDepartmentAssignments(projectDepartmentMap[projectId], validDepartmentIds);
  }, [draft.projectId, projectDepartmentMap, validDepartmentIds]);

  const draftDepartmentNames = useMemo(() => (
    draftProjectDepartmentIds
      .map((departmentId) => departmentById.get(departmentId) || '')
      .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }))
      .filter(Boolean)
  ), [draftProjectDepartmentIds, departmentById]);

  const handleToggleDraftProjectDepartment = async (departmentId: string, checked: boolean) => {
    if (!isAdminUser) return;
    const projectId = String(draft.projectId || '').trim();
    if (!projectId) return;

    const nextIds = checked
      ? [...draftProjectDepartmentIds, departmentId]
      : draftProjectDepartmentIds.filter((id) => id !== departmentId);
    const normalized = normalizeDepartmentAssignments(nextIds, validDepartmentIds);
    const nextMap: Record<string, string[]> = { ...projectDepartmentMap };
    if (normalized.length === 0) delete nextMap[projectId];
    else nextMap[projectId] = normalized;

    try {
      setIsSavingProjectDepartmentRouting(true);
      await updateUserPreferences({
        ...preferencesTenantWriteSpread(tenantIdForPrefs),
        preferences: {
          ermProjectDepartments: nextMap,
        },
      }).unwrap();
      await refetchUserPreferences();
    } catch (error) {
      console.error('Failed to update project department routing:', error);
      alert('Unable to update department routing for this project.');
    } finally {
      setIsSavingProjectDepartmentRouting(false);
    }
  };

  const formatDueDate = (value: unknown): string => {
    const raw = String(value || '').trim();
    if (!raw) return '—';
    try {
      return toLocaleDateStringInTimezone(
        raw,
        'en-US',
        { month: 'long', day: 'numeric', year: 'numeric' },
        tenantTimezone
      );
    } catch {
      return raw;
    }
  };

  const getProjectName = (projectId: unknown): string => {
    const id = String(projectId || '').trim();
    if (!id) return '—';
    const project = projectById.get(id);
    return String(project?.title || '').trim() || id;
  };

  const getAssigneeLabel = (assigneeId: unknown): string => {
    const normalized = String(assigneeId || '').trim();
    if (!normalized) return 'Unassigned';
    if (normalized === currentUserId) return 'You';
    return userLabelById.get(normalized) || 'Assigned';
  };

  const handleToggleDone = async (task: ErmTask, nextDone: boolean) => {
    try {
      await updateTask({ taskId: task.id, done: nextDone }).unwrap();
      if (draft.id === task.id) {
        setDraft((prev) => ({ ...prev, done: nextDone }));
      }
      await refetchTasks();
    } catch (error) {
      console.error('Failed to update task done status:', error);
    }
  };

  const handleArchiveTask = async (taskId: string) => {
    if (!taskId) return;
    const confirmed = window.confirm('Archive this task? You can permanently delete it from Archive.');
    if (!confirmed) return;
    try {
      await updateTask({ taskId, archived: true }).unwrap();
      if (draft.id === taskId) {
        setShowEditor(false);
      }
      await refetchTasks();
    } catch (error) {
      console.error('Failed to archive task:', error);
    }
  };

  const handleSaveTask = async () => {
    const subject = String(draft.subject || '').trim();
    if (!subject) return;

    try {
      const nextVisibility = draft.visibility === 'personal' ? 'personal' : 'global';
      const nextOwnerId = nextVisibility === 'personal'
        ? String(draft.ownerId || currentUserId || inferredCurrentUserId || '').trim()
        : '';
      if (draft.id) {
        await updateTask({
          taskId: draft.id,
          subject,
          phase: draft.phase || 'Phase unassigned',
          group: draft.group || DEFAULT_GROUP,
          dueDate: draft.dueDate || '',
          done: Boolean(draft.done),
          ownerId: nextOwnerId,
          visibility: nextVisibility,
          assigneeId: draft.assigneeId || '',
          description: draft.description || '',
        }).unwrap();
      } else {
        if (!draft.projectId) {
          alert('Please choose a project before saving this task.');
          return;
        }
        await createTask({
          projectId: draft.projectId,
          subject,
          phase: draft.phase || 'Phase unassigned',
          group: draft.group || DEFAULT_GROUP,
          dueDate: draft.dueDate || '',
          done: Boolean(draft.done),
          ownerId: nextOwnerId,
          visibility: nextVisibility,
          assigneeId: draft.assigneeId || currentUserId || inferredCurrentUserId || '',
          description: draft.description || '',
        }).unwrap();
      }
      setShowEditor(false);
      await refetchTasks();
    } catch (error) {
      console.error('Failed to save task:', error);
    }
  };

  const isSavingTask = isCreatingTask || isUpdatingTask;
  const isBusy = isLoadingTasks || isLoadingProjects;

  return (
    <div className="erm-tasks">
      <ERMSectionHeader
        title="Tasks"
        showHelp
        searchPlaceholder="Search tasks"
        searchValue={search}
        onSearchChange={setSearch}
      />

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3 mt-3">
        <Button
          variant="primary"
          size="sm"
          className="d-flex align-items-center"
          style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          onClick={openCreatePanel}
        >
          + Task
        </Button>

        <Dropdown align="end">
          <Dropdown.Toggle
            variant="outline-secondary"
            size="sm"
            className="d-flex align-items-center"
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          >
            <Funnel size={12} className="me-1" />
            {activeStatusLabel}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item active={statusFilter === 'all'} onClick={() => setStatusFilter('all')}>
              All statuses
            </Dropdown.Item>
            <Dropdown.Item active={statusFilter === 'done'} onClick={() => setStatusFilter('done')}>
              Done
            </Dropdown.Item>
            <Dropdown.Item active={statusFilter === 'todo'} onClick={() => setStatusFilter('todo')}>
              To-do
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>

        <Dropdown
          align="end"
          show={showOwnerFilter}
          autoClose="outside"
          style={{ order: 99, marginLeft: 'auto' }}
          onToggle={(isOpen) => {
            setShowOwnerFilter(isOpen);
            if (isOpen) {
              setOwnerFilterSearchQuery('');
              setOwnerFilterTab('owners');
              setHoveredOwnerRowKey(null);
            } else {
              setHoveredOwnerRowKey(null);
            }
          }}
        >
          <Dropdown.Toggle
            variant="outline-secondary"
            size="sm"
            className="d-flex align-items-center"
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          >
            {selectedOwnerButtonLabel}
          </Dropdown.Toggle>
          <Dropdown.Menu style={{ width: '320px', padding: 0, borderRadius: '0.5rem', overflow: 'hidden' }}>
            <div className="p-2 border-bottom" onClick={(event) => event.stopPropagation()}>
              <InputGroup size="sm">
                <InputGroup.Text style={{ backgroundColor: '#fff', borderRight: 'none' }}>
                  <Search size={14} />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search owner or filter"
                  value={ownerFilterSearchQuery}
                  onChange={(event) => setOwnerFilterSearchQuery(event.target.value)}
                  style={{ borderLeft: 'none', fontSize: '0.75rem' }}
                />
              </InputGroup>
            </div>
            <div className="d-flex border-bottom" onClick={(event) => event.stopPropagation()}>
              <button
                type="button"
                onClick={() => setOwnerFilterTab('favorites')}
                style={{
                  flex: 1,
                  border: 'none',
                  borderBottom: ownerFilterTab === 'favorites' ? '2px solid #0d6efd' : '2px solid transparent',
                  backgroundColor: 'transparent',
                  color: ownerFilterTab === 'favorites' ? '#0d6efd' : '#6c757d',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  padding: '0.5rem 0.25rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.15rem',
                }}
              >
                <Star size={15} />
                <span>Favorites</span>
              </button>
              <button
                type="button"
                onClick={() => setOwnerFilterTab('owners')}
                style={{
                  flex: 1,
                  border: 'none',
                  borderBottom: ownerFilterTab === 'owners' ? '2px solid #0d6efd' : '2px solid transparent',
                  backgroundColor: 'transparent',
                  color: ownerFilterTab === 'owners' ? '#0d6efd' : '#6c757d',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  padding: '0.5rem 0.25rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.15rem',
                }}
              >
                <Person size={15} />
                <span>Owners</span>
              </button>
              <button
                type="button"
                onClick={() => setOwnerFilterTab('filters')}
                style={{
                  flex: 1,
                  border: 'none',
                  borderBottom: ownerFilterTab === 'filters' ? '2px solid #0d6efd' : '2px solid transparent',
                  backgroundColor: 'transparent',
                  color: ownerFilterTab === 'filters' ? '#0d6efd' : '#6c757d',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  padding: '0.5rem 0.25rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.15rem',
                }}
              >
                <Funnel size={15} />
                <span>Filters</span>
              </button>
            </div>
            <div style={{ maxHeight: '280px', overflowY: 'auto', overflowX: 'hidden' }} onClick={(event) => event.stopPropagation()}>
              {ownerFilterTab === 'owners' && (
                <div className="py-1">
                  {visibleOwnerOptions.length === 0 ? (
                    <div className="px-3 py-2 text-muted" style={{ fontSize: '0.75rem' }}>
                      No owners found.
                    </div>
                  ) : (
                    visibleOwnerOptions.map((ownerOption) => {
                      const isEveryone = ownerOption.id === OWNER_EVERYONE_ID;
                      const isFavorite = favoriteOwnerKeys.includes(ownerOption.id);
                      const isSelected = selectedOwnerId === ownerOption.id;
                      const hoverKey = normalizeOwnerKey(ownerOption.label) || ownerOption.id;
                      const isRowHovered = hoveredOwnerRowKey === hoverKey;
                      const rowTextColor = isRowHovered ? '#ffffff' : '#212529';
                      return (
                        <button
                          key={ownerOption.id}
                          type="button"
                          onClick={() => {
                            setSelectedOwnerId(ownerOption.id);
                            setShowOwnerFilter(false);
                          }}
                          onMouseEnter={() => setHoveredOwnerRowKey(hoverKey)}
                          onMouseLeave={() => setHoveredOwnerRowKey((prev) => (prev === hoverKey ? null : prev))}
                          style={{
                            width: '100%',
                            border: 'none',
                            backgroundColor: isRowHovered ? '#0d6efd' : 'transparent',
                            textAlign: 'left',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '0.55rem 0.75rem',
                            fontSize: '0.8rem',
                            borderRadius: '0.35rem',
                            boxSizing: 'border-box',
                          }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <Person size={14} style={{ color: isRowHovered ? '#ffffff' : '#6c757d' }} />
                            <span style={{ fontWeight: isEveryone ? 600 : 500, color: rowTextColor }}>{ownerOption.label}</span>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            {isEveryone ? (
                              <span style={{ color: isRowHovered ? '#ffffff' : '#adb5bd', lineHeight: 1, display: 'inline-flex' }}>
                                <Star size={14} />
                              </span>
                            ) : (
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  toggleFavoriteOwner(ownerOption.id);
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    toggleFavoriteOwner(ownerOption.id);
                                  }
                                }}
                                style={{
                                  color: isFavorite ? '#f4b400' : (isRowHovered ? '#ffffff' : '#adb5bd'),
                                  lineHeight: 1,
                                  display: 'inline-flex',
                                }}
                              >
                                {isFavorite ? <StarFill size={14} /> : <Star size={14} />}
                              </span>
                            )}
                            {isSelected ? <Check size={15} style={{ color: isRowHovered ? '#ffffff' : '#111827' }} /> : null}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              )}
              {ownerFilterTab === 'filters' && (
                <div className="py-1">
                  {visibleScopeFilterOptions.length === 0 ? (
                    <div className="px-3 py-2 text-muted" style={{ fontSize: '0.75rem' }}>
                      No filters found.
                    </div>
                  ) : (
                    visibleScopeFilterOptions.map((filterOption) => {
                      const isSelected = assigneeFilter === filterOption.id;
                      const isFavorite = favoriteAssigneeFilterIds.includes(filterOption.id);
                      return (
                        <button
                          key={filterOption.id}
                          type="button"
                          onClick={() => setAssigneeFilter(filterOption.id)}
                          style={{
                            width: '100%',
                            border: 'none',
                            backgroundColor: 'transparent',
                            textAlign: 'left',
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: '0.5rem',
                            padding: '0.6rem 0.75rem',
                          }}
                        >
                          <div>
                            <div style={{ fontSize: '0.79rem', fontWeight: 500, color: '#212529' }}>{filterOption.label}</div>
                            <div style={{ fontSize: '0.69rem', color: '#6c757d', marginTop: '0.15rem' }}>
                              {filterOption.description}
                            </div>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            {filterOption.id !== 'all' ? (
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  toggleFavoriteAssigneeFilter(filterOption.id);
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    toggleFavoriteAssigneeFilter(filterOption.id);
                                  }
                                }}
                                style={{ color: isFavorite ? '#f4b400' : '#adb5bd', lineHeight: 1, display: 'inline-flex' }}
                              >
                                {isFavorite ? <StarFill size={14} /> : <Star size={14} />}
                              </span>
                            ) : null}
                            {isSelected ? <Check size={15} style={{ color: '#111827' }} /> : null}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              )}
              {ownerFilterTab === 'favorites' && (
                <div className="py-1">
                  {visibleFavoriteOwnerOptions.length === 0 && visibleFavoriteScopeFilterOptions.length === 0 ? (
                    <div className="px-3 py-2 text-muted" style={{ fontSize: '0.75rem' }}>
                      No favorites yet.
                    </div>
                  ) : (
                    <>
                      {visibleFavoriteOwnerOptions.length > 0 ? (
                        <div className="px-3 pt-2 pb-1 text-muted" style={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.02em' }}>
                          Owners
                        </div>
                      ) : null}
                      {visibleFavoriteOwnerOptions.map((ownerOption) => {
                        const isSelected = selectedOwnerId === ownerOption.id;
                        return (
                          <button
                            key={`task-favorite-owner-${ownerOption.id}`}
                            type="button"
                            onClick={() => {
                              setSelectedOwnerId(ownerOption.id);
                              setShowOwnerFilter(false);
                            }}
                            style={{
                              width: '100%',
                              border: 'none',
                              backgroundColor: 'transparent',
                              textAlign: 'left',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '0.55rem 0.75rem',
                              fontSize: '0.8rem',
                            }}
                          >
                            <div className="d-flex align-items-center gap-2">
                              <Person size={14} style={{ color: '#6c757d' }} />
                              <span style={{ fontWeight: 500, color: '#212529' }}>{ownerOption.label}</span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <StarFill size={14} style={{ color: '#f4b400' }} />
                              {isSelected ? <Check size={15} style={{ color: '#111827' }} /> : null}
                            </div>
                          </button>
                        );
                      })}
                      {visibleFavoriteScopeFilterOptions.length > 0 ? (
                        <div className="px-3 pt-2 pb-1 text-muted" style={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.02em' }}>
                          Filters
                        </div>
                      ) : null}
                      {visibleFavoriteScopeFilterOptions.map((filterOption) => {
                        const isSelected = assigneeFilter === filterOption.id;
                        return (
                          <button
                            key={`task-favorite-filter-${filterOption.id}`}
                            type="button"
                            onClick={() => setAssigneeFilter(filterOption.id)}
                            style={{
                              width: '100%',
                              border: 'none',
                              backgroundColor: 'transparent',
                              textAlign: 'left',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '0.55rem 0.75rem',
                              fontSize: '0.8rem',
                            }}
                          >
                            <div className="d-flex align-items-center gap-2">
                              <Funnel size={14} style={{ color: '#6c757d' }} />
                              <span style={{ fontWeight: 500, color: '#212529' }}>{filterOption.label}</span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <StarFill size={14} style={{ color: '#f4b400' }} />
                              {isSelected ? <Check size={15} style={{ color: '#111827' }} /> : null}
                            </div>
                          </button>
                        );
                      })}
                    </>
                  )}
                </div>
              )}
            </div>
          </Dropdown.Menu>
        </Dropdown>

        <Dropdown align="end">
          <Dropdown.Toggle
            variant="outline-secondary"
            size="sm"
            className="d-flex align-items-center"
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          >
            <Building size={12} className="me-1" />
            {activeDepartmentLabel}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {sortedDepartments.map((department) => {
              const isFocused = focusedDepartmentIdSet.has(department.id);
              return (
                <Dropdown.Item
                  key={department.id}
                  active={isFocused}
                  onClick={() => { void handleToggleFocusedDepartment(department.id); }}
                >
                  {isFocused ? '✓ ' : ''}{department.name}
                </Dropdown.Item>
              );
            })}
            <Dropdown.Divider />
            <Dropdown.Item onClick={() => { void handleClearFocusedDepartments(); }}>
              Show all departments
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>

        <span className="text-muted" style={{ fontSize: '0.75rem' }}>
          {taskCountLabel}
        </span>
      </div>
      {focusedDepartmentIds.length > 0 ? (
        <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
          <span className="text-muted" style={{ fontSize: '0.75rem' }}>Focused:</span>
          {sortedFocusedDepartmentIds.map((departmentId) => (
            <Badge key={departmentId} bg="primary" className="d-flex align-items-center gap-1" style={{ fontSize: '0.7rem' }}>
              {departmentById.get(departmentId) || departmentId}
            </Badge>
          ))}
          <Button
            variant="link"
            className="p-0 text-decoration-none"
            style={{ fontSize: '0.75rem' }}
            onClick={() => { void handleClearFocusedDepartments(); }}
          >
            Clear
          </Button>
        </div>
      ) : null}

      <Card className="crm-table-card">

        <div className="table-responsive">
          <Table hover size="sm" className="mb-0 align-middle crm-table">
            <thead className="table-light">
              <tr>
                <th style={{ width: '64px', fontSize: '0.75rem' }}>Done</th>
                <th style={{ width: '260px', minWidth: '260px', fontSize: '0.75rem' }}>Subject</th>
                <th style={{ width: '180px', fontSize: '0.75rem' }}>Project</th>
                <th style={{ width: '180px', fontSize: '0.75rem' }}>Department</th>
                <th style={{ width: '170px', fontSize: '0.75rem' }}>Phase</th>
                <th style={{ width: '150px', fontSize: '0.75rem' }}>Group</th>
                <th style={{ width: '170px', fontSize: '0.75rem' }}>Due date</th>
                <th style={{ width: '140px', fontSize: '0.75rem' }}>Assignee</th>
                <th style={{ width: '56px', fontSize: '0.75rem' }} />
              </tr>
            </thead>
            <tbody>
              {isBusy && (
                <tr>
                  <td colSpan={9} className="text-center py-4 text-muted">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading tasks...
                  </td>
                </tr>
              )}

              {!isBusy && visibleTasks.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center py-4 text-muted">
                    No tasks found for this filter.
                  </td>
                </tr>
              )}

              {!isBusy && visibleTasks.map((task) => {
                const taskDepartmentNames = getTaskDepartmentNames(task);
                const fullSubject = String(task.subject || '').trim() || 'Untitled task';
                const subjectDisplay = getTaskSubjectDisplay(task.subject);
                return (
                <tr key={task.id}>
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={Boolean(task.done)}
                      onChange={(event) => handleToggleDone(task, event.target.checked)}
                    />
                  </td>
                  <td style={{ width: '260px', minWidth: '260px' }}>
                    <OverlayTrigger
                      placement="top-start"
                      delay={{ show: 100, hide: 0 }}
                      overlay={(
                        <Tooltip id={`task-subject-tooltip-${task.id}`}>
                          {fullSubject}
                        </Tooltip>
                      )}
                    >
                      <Button
                        variant="link"
                        className="p-0 text-start text-decoration-none"
                        style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}
                        onClick={() => openEditPanel(task)}
                      >
                        {subjectDisplay}
                      </Button>
                    </OverlayTrigger>
                  </td>
                  <td style={{ fontSize: '0.8rem' }}>{getProjectName(task.projectId)}</td>
                  <td style={{ fontSize: '0.78rem' }}>
                    <div className="d-flex flex-wrap gap-1">
                      {taskDepartmentNames.length > 0 ? (
                        taskDepartmentNames.slice(0, 2).map((name) => (
                          <Badge key={`${task.id}-${name}`} bg="light" text="dark" style={{ fontSize: '0.66rem' }}>
                            {name}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </div>
                  </td>
                  <td style={{ fontSize: '0.8rem' }}>{task.phase || 'Phase unassigned'}</td>
                  <td style={{ fontSize: '0.8rem' }}>{task.group || DEFAULT_GROUP}</td>
                  <td style={{ fontSize: '0.8rem' }}>{formatDueDate(task.dueDate)}</td>
                  <td style={{ fontSize: '0.8rem' }}>{getAssigneeLabel(task.assigneeId)}</td>
                  <td className="text-end">
                    <Dropdown align="end">
                      <Dropdown.Toggle
                        variant="link"
                        size="sm"
                        className="text-dark p-0 text-decoration-none"
                        id={`task-actions-${task.id}`}
                      >
                        <ThreeDotsVertical size={14} />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => openEditPanel(task)}>Edit task</Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => handleArchiveTask(task.id)}
                          disabled={isUpdatingTask}
                        >
                          Archive task
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </Card>

      <Offcanvas
        show={showEditor}
        onHide={() => setShowEditor(false)}
        placement="end"
        style={{ width: 'min(920px, 95vw)' }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="d-flex align-items-center gap-2">
            <Check2Circle size={18} className="text-muted" />
            Task
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="d-flex flex-column">
          <Row className="g-3 flex-grow-1">
            <Col lg={8}>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label className="small text-muted">Subject</Form.Label>
                  <Form.Control
                    value={draft.subject}
                    onChange={(event) => setDraft((prev) => ({ ...prev, subject: event.target.value }))}
                    placeholder="Task subject"
                  />
                </Form.Group>

                <h6 className="mb-2">Task details</h6>

                <Form.Group className="mb-3">
                  <Form.Label className="small text-muted">Owner</Form.Label>
                  <Form.Select
                    value={draft.visibility === 'personal' ? 'personal' : 'global'}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        visibility: event.target.value === 'personal' ? 'personal' : 'global',
                        ownerId: event.target.value === 'personal'
                          ? (currentUserId || prev.ownerId || inferredCurrentUserId)
                          : '',
                      }))
                    }
                  >
                    <option value="global">{DEFAULT_OWNER_FILTER_LABEL}</option>
                    <option value="personal">{selfOwnerBaseLabel}</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="small text-muted">Visibility</Form.Label>
                  <Form.Select
                    value={draft.visibility === 'personal' ? 'personal' : 'global'}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        visibility: event.target.value === 'personal' ? 'personal' : 'global',
                        ownerId: event.target.value === 'personal'
                          ? (currentUserId || prev.ownerId || inferredCurrentUserId)
                          : '',
                      }))
                    }
                  >
                    <option value="global">Global (visible for everyone)</option>
                    <option value="personal">Personal (private)</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="small text-muted">Assignee (optional)</Form.Label>
                  <Form.Select
                    value={draft.assigneeId || '__unassigned__'}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        assigneeId: event.target.value === '__unassigned__' ? '' : event.target.value,
                      }))
                    }
                  >
                    <option value="__unassigned__">Unassigned</option>
                    {Array.from(userLabelById.entries()).map(([userId, label]) => (
                      <option key={`task-assignee-${userId}`} value={userId}>
                        {userId === currentUserId ? `${label} (you)` : label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="small text-muted">Due date (optional)</Form.Label>
                  <Form.Control
                    type="date"
                    value={draft.dueDate}
                    onChange={(event) => setDraft((prev) => ({ ...prev, dueDate: event.target.value }))}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="small text-muted">Project</Form.Label>
                  <Form.Select
                    value={draft.projectId}
                    disabled={Boolean(draft.id)}
                    onChange={(event) => setDraft((prev) => ({ ...prev, projectId: event.target.value }))}
                  >
                    <option value="">Select project</option>
                    {normalizedProjects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {String(project.title || '').trim() || project.id}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="small text-muted">Departments</Form.Label>
                  {isAdminUser ? (
                    <>
                      <Dropdown autoClose="outside">
                        <Dropdown.Toggle
                          variant="outline-secondary"
                          size="sm"
                          style={{ fontSize: '0.74rem' }}
                          disabled={!String(draft.projectId || '').trim() || isSavingProjectDepartmentRouting}
                        >
                          {isSavingProjectDepartmentRouting ? 'Saving...' : 'Assign departments'}
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="px-2 py-2" style={{ minWidth: 260, maxHeight: 220, overflowY: 'auto' }}>
                          {sortedDepartments.map((department) => (
                            <Form.Check
                              key={`task-draft-project-${department.id}`}
                              id={`task-draft-project-${department.id}`}
                              type="checkbox"
                              className="small mb-1"
                              label={department.name}
                              checked={draftProjectDepartmentIds.includes(department.id)}
                              disabled={!String(draft.projectId || '').trim() || isSavingProjectDepartmentRouting}
                              onChange={(event) => {
                                void handleToggleDraftProjectDepartment(department.id, event.target.checked);
                              }}
                            />
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                      <div className="border rounded p-2 bg-light mt-2">
                        {draftDepartmentNames.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1">
                            {draftDepartmentNames.map((name) => (
                              <Badge key={`draft-dept-${name}`} bg="light" text="dark" style={{ fontSize: '0.68rem' }}>
                                {name}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted small">No departments assigned to this project.</span>
                        )}
                      </div>
                      <Form.Text className="text-muted">
                        This updates department routing for the selected project.
                      </Form.Text>
                    </>
                  ) : (
                    <>
                      <div className="border rounded p-2 bg-light">
                        {draftDepartmentNames.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1">
                            {draftDepartmentNames.map((name) => (
                              <Badge key={`draft-dept-${name}`} bg="light" text="dark" style={{ fontSize: '0.68rem' }}>
                                {name}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted small">No departments assigned to this project.</span>
                        )}
                      </div>
                      <Form.Text className="text-muted">
                        Only admins can edit department routing.
                      </Form.Text>
                    </>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="small text-muted">Phase</Form.Label>
                  <Form.Select
                    value={draft.phase}
                    onChange={(event) => setDraft((prev) => ({ ...prev, phase: event.target.value }))}
                  >
                    {PHASE_OPTIONS.map((phase) => (
                      <option key={phase} value={phase}>
                        {phase}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="small text-muted">Group</Form.Label>
                  <Form.Control
                    value={draft.group}
                    onChange={(event) => setDraft((prev) => ({ ...prev, group: event.target.value }))}
                    placeholder="Ungrouped"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="small text-muted">Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={draft.description}
                    onChange={(event) => setDraft((prev) => ({ ...prev, description: event.target.value }))}
                    placeholder="Add details..."
                  />
                </Form.Group>
              </Form>
            </Col>

            <Col lg={4}>
              <div className="border rounded p-3 h-100 d-flex flex-column align-items-center justify-content-center text-center">
                <div className="text-muted fw-semibold mb-1">Subtasks</div>
                <div className="text-muted small mb-3">
                  Use subtasks to split work into manageable pieces.
                </div>
                <Badge bg="light" text="dark">
                  + Subtask (coming soon)
                </Badge>
              </div>
            </Col>
          </Row>

          <div className="d-flex align-items-center justify-content-between border-top pt-3 mt-3">
            <Form.Check
              type="checkbox"
              id="task-mark-done"
              label="Mark as done"
              checked={Boolean(draft.done)}
              onChange={(event) => setDraft((prev) => ({ ...prev, done: event.target.checked }))}
            />
            <div className="d-flex align-items-center gap-2">
              <Button variant="outline-secondary" onClick={() => setShowEditor(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveTask}
                disabled={isSavingTask || !String(draft.subject || '').trim()}
              >
                {isSavingTask ? <Spinner animation="border" size="sm" /> : 'Save'}
              </Button>
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
}
